/**
 * Module 131 (WP-131.7, D6/D7) — the staged unlock framework, pure.
 *
 * Wave 3 systems open one at a time, by TOTAL wins (every track and the Daily:
 * the `wins` milestone counter), at the thresholds in the economy registry
 * (`ECONOMY.unlocks`, Economy_Rules rule 10). Milestones is there from the start.
 *
 * A system is only ever shown once its own module has built it (D7: a locked
 * card promises something reachable). Until then it is `hidden` — not locked,
 * not teased. The module that ships a system flips its {@link SHIPPED} entry and
 * renders its open state; this framework owns the locked state, the tap
 * explainer and the one-time intro card.
 */
import { ECONOMY } from '../constants/economy-registry';
/** Every staged system, in unlock order (ties keep registry order). */
export const FEATURE_IDS = Object.keys(ECONOMY.unlocks)
    .map((id, i) => ({ id, i }))
    .sort((a, b) => ECONOMY.unlocks[a.id] - ECONOMY.unlocks[b.id] || a.i - b.i)
    .map(({ id }) => id);
/**
 * D7 — which systems their modules have shipped. All false at Module 131: Missions
 * (133), Win Streak (134), Boosters (135), Collection (136) and Themes (119) each
 * flip their own entry when they land.
 */
export const SHIPPED = {
    // Module 133 (WP-133.4) — Daily Missions shipped.
    missions: true,
    winStreak: false,
    boosters: false,
    collection: false,
    themes: false,
};
export function unlockThreshold(id) {
    return ECONOMY.unlocks[id];
}
export function isShipped(id, shipped = SHIPPED) {
    return shipped[id];
}
export function isUnlocked(id, wins) {
    return wins >= unlockThreshold(id);
}
export function featureState(id, wins, shipped = SHIPPED) {
    if (!isShipped(id, shipped))
        return { kind: 'hidden' };
    const need = unlockThreshold(id);
    return wins >= need ? { kind: 'open' } : { kind: 'locked', need, left: need - wins };
}
export function featureStates(wins, shipped = SHIPPED) {
    return Object.fromEntries(FEATURE_IDS.map((id) => [id, featureState(id, wins, shipped)]));
}
/**
 * The system whose one-time intro card is owed: the first (in unlock order) that
 * is shipped, unlocked and not yet introduced. One at a time — the next waits for
 * the next quiet home. A player who was already past a threshold when its module
 * shipped still gets the intro once: that is their first sight of it.
 */
export function introDue(wins, seen, shipped = SHIPPED) {
    return (FEATURE_IDS.find((id) => isShipped(id, shipped) && isUnlocked(id, wins) && !seen.includes(id)) ?? null);
}
